import { toLocaleTimeStringInTz, toLocaleDateStringInTz } from './dateFormatters';

export interface ParsedWhatsAppMessage {
  timestamp: string | null;
  formattedDate: string;
  sender: 'user' | 'assistant';
  body: string;
  dateGroup: string;
  messageType?: string;
  audioUrl?: string | null;
  videoUrl?: string | null;
  deliveryStatus?: string | null;
}

const BRACKET_RE = /^\[(\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2})\]\s+(user|assistant):/;
const ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
const IS_NEXT_MSG_RE = /^\[(\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2})\]\s+(user|assistant):|^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

export const parseWhatsAppThread = (conversationText: string, tz?: string): ParsedWhatsAppMessage[] => {
  if (!conversationText || conversationText.trim() === '') {
    return [];
  }

  const normalized = conversationText.replace(/^"+|"+$/g, '').replace(/\r\n/g, '\n');
  const messages: ParsedWhatsAppMessage[] = [];
  const lines = normalized.split('\n');
  let i = 0;

  while (i < lines.length) {
    const line = lines[i].trim();

    const bracketMatch = line.match(BRACKET_RE);
    if (bracketMatch) {
      const timestamp = bracketMatch[1].replace(' ', 'T') + ':00';
      const sender = bracketMatch[2] as 'user' | 'assistant';
      i++;

      let messageText = '';
      while (i < lines.length) {
        const currentLine = lines[i];
        if (currentLine.trim().match(IS_NEXT_MSG_RE)) break;
        if (currentLine.trim() === '' && messageText) {
          i++;
          break;
        }
        if (currentLine.trim() !== '') {
          messageText += (messageText ? '\n' : '') + currentLine;
        }
        i++;
      }

      const trimmedBody = messageText.trim();
      if (trimmedBody) {
        messages.push({
          timestamp,
          formattedDate: formatMessageTime(timestamp, tz),
          sender,
          body: trimmedBody,
          dateGroup: getDateGroup(timestamp, tz),
        });
      }
      continue;
    }

    if (line.match(ISO_RE)) {
      const timestamp = line;
      i++;

      if (i < lines.length) {
        const senderLine = lines[i].trim();
        if (senderLine === 'user:' || senderLine === 'assistant:') {
          const sender = senderLine.replace(':', '') as 'user' | 'assistant';
          i++;

          let messageText = '';
          let foundMessageText = false;

          while (i < lines.length) {
            const currentLine = lines[i];

            if (currentLine.match(IS_NEXT_MSG_RE)) {
              break;
            }

            const trimmedLine = currentLine.trim();

            if (trimmedLine.startsWith('sender:') || trimmedLine.startsWith('timestamp:') || trimmedLine.startsWith('replied to:')) {
              i++;
              continue;
            }

            if (trimmedLine.startsWith('message text:')) {
              messageText = trimmedLine.replace('message text:', '').trim();
              foundMessageText = true;
              i++;
              continue;
            }

            if (trimmedLine === '') {
              if (messageText || foundMessageText) {
                break;
              }
              i++;
              continue;
            }

            if (!foundMessageText && !trimmedLine.startsWith('sender:') && !trimmedLine.startsWith('timestamp:')) {
              if (messageText) {
                messageText += '\n' + currentLine;
              } else {
                messageText = currentLine;
              }
            }

            i++;
          }

          const trimmedBody = messageText.trim();
          if (trimmedBody) {
            messages.push({
              timestamp,
              formattedDate: formatMessageTime(timestamp, tz),
              sender,
              body: trimmedBody,
              dateGroup: getDateGroup(timestamp, tz),
            });
          }
          continue;
        }
      }
    }
    i++;
  }

  return messages;
};

const formatMessageTime = (timestamp: string, tz?: string): string => {
  try {
    const date = new Date(timestamp);
    return toLocaleTimeStringInTz(date, tz, 'en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return '';
  }
};

const getDateGroup = (timestamp: string, tz?: string): string => {
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';

    return toLocaleDateStringInTz(date, tz, 'en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  } catch {
    return '';
  }
};
